/**
 * Tabs — the ARIA tab pattern, shared so no app has to rebuild it.
 *
 * A `role="tab"` with `aria-selected` is only half the contract. This supplies
 * the other half: ids wiring each tab to its panel, roving tabIndex so the
 * strip is one stop in the tab order, arrow/Home/End navigation, and panels
 * that name the tab they belong to.
 *
 * The strip scrolls rather than wraps, so six tabs still fit at 375px.
 */
import { useId, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '../lib/utils';

export interface TabItem<K extends string> {
  key: K;
  label: string;
  /** Rendered as a pill after the label. Omitted when undefined. */
  count?: number;
}

export function tabId(prefix: string, key: string): string {
  return `${prefix}-tab-${key}`;
}

export function tabPanelId(prefix: string, key: string): string {
  return `${prefix}-panel-${key}`;
}

export function TabStrip<K extends string>({
  tabs,
  value,
  onChange,
  label,
  idPrefix,
  className,
}: {
  tabs: TabItem<K>[];
  value: K;
  onChange: (key: K) => void;
  /** Names the tablist for assistive technology. */
  label: string;
  /** Pass the same prefix to TabPanel so the ids line up. */
  idPrefix: string;
  className?: string;
}) {
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = tabs.findIndex((tab) => tab.key === value);
    if (index === -1) return;
    let next = index;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;
    event.preventDefault();
    onChange(tabs[next].key);
    document.getElementById(tabId(idPrefix, tabs[next].key))?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      className={cn(
        'scrollbar-hide -mx-3 flex gap-1 overflow-x-auto border-b border-border px-3 sm:mx-0 sm:px-0',
        className,
      )}
    >
      {tabs.map((tab) => {
        const selected = tab.key === value;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            id={tabId(idPrefix, tab.key)}
            aria-selected={selected}
            aria-controls={tabPanelId(idPrefix, tab.key)}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.key)}
            className={cn(
              'min-h-11 flex-shrink-0 whitespace-nowrap border-b-2 px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary sm:px-4',
              selected
                ? 'border-primary text-primary'
                : 'border-transparent text-text-muted hover:text-text',
            )}
          >
            {tab.label}
            {typeof tab.count === 'number' && (
              <span className="ml-1.5 rounded-full bg-surface-3 px-1.5 py-0.5 text-[11px] font-semibold text-text-muted">
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({
  idPrefix,
  tabKey,
  children,
  className,
}: {
  idPrefix: string;
  tabKey: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      role="tabpanel"
      id={tabPanelId(idPrefix, tabKey)}
      aria-labelledby={tabId(idPrefix, tabKey)}
      tabIndex={0}
      className={cn('focus-visible:outline-none', className)}
    >
      {children}
    </div>
  );
}

/** Stable id prefix when a caller has no natural one. */
export function useTabPrefix(): string {
  return useId();
}
